import { PenIcon, PlusIcon, TrashIcon } from "@xola/icons";
import { Badge } from "@xola/ui-kit";
import { truncate } from "lodash";
import React, { useState } from "react";
import { BaseSchedule, BaseScheduleType } from "types";
import { createBaseSchedule } from "../../models/BaseSchedule";
import { BaseScheduleEditorModal } from "../BaseSchedule/BaseScheduleEditorModal";
import { BaseScheduleSummary } from "../BaseSchedule/BaseScheduleSummary";
import { NoTranslate } from "../Common/NoTranslate";
import { SecondaryButton } from "../Common/SecondaryButton";

interface GiftOfferScheduleEditorProps {
    title: string;
    description: string;
    windowSchedule: BaseSchedule | null;
    blackoutSchedules: BaseSchedule[];
    onWindowScheduleChange: (windowSchedule: BaseSchedule | null) => void;
    onBlackoutSchedulesChange: (blackoutSchedules: BaseSchedule[]) => void;
}

export const GiftOfferScheduleEditor = ({
    title,
    description,
    windowSchedule,
    blackoutSchedules,
    onWindowScheduleChange,
    onBlackoutSchedulesChange,
}: GiftOfferScheduleEditorProps) => {
    const [selectedWindowSchedule, setSelectedWindowSchedule] = useState<BaseSchedule | null>(null);
    const [selectedBlackoutScheduleIndex, setSelectedBlackoutScheduleIndex] = useState<number | null>(null);
    const [showCreateBlackoutScheduleModal, setShowCreateBlackoutScheduleModal] = useState(false);

    const handleCreatePurchaseWindowSchedule = () => {
        setSelectedWindowSchedule(createBaseSchedule({ type: BaseScheduleType.AVAILABLE }));
    };

    const handleUpdatePurchaseWindowSchedule = (schedule: BaseSchedule) => {
        onWindowScheduleChange(schedule);
    };

    const handleRemovePurchaseWindowSchedule = () => {
        onWindowScheduleChange(null);
    };

    const handleCreateBlackoutSchedule = () => {
        setShowCreateBlackoutScheduleModal(true);
    };

    const handleUpdateBlackoutSchedule = (schedule: BaseSchedule) => {
        if (selectedBlackoutScheduleIndex === null) {
            onBlackoutSchedulesChange([...blackoutSchedules, schedule]);
        } else {
            const updatedSchedules = blackoutSchedules.map((blackoutSchedule, index) => {
                return index === selectedBlackoutScheduleIndex ? schedule : blackoutSchedule;
            });

            onBlackoutSchedulesChange(updatedSchedules);
        }
    };

    const handleRemoveBlackoutSchedule = (removeIndex: number) => {
        const updatedSchedules = blackoutSchedules.filter((schedule, index) => index !== removeIndex);
        onBlackoutSchedulesChange(updatedSchedules);
    };

    const selectedBlackoutSchedule =
        selectedBlackoutScheduleIndex === null
            ? createBaseSchedule({ type: BaseScheduleType.UNAVAILABLE })
            : blackoutSchedules[selectedBlackoutScheduleIndex];

    return (
        <div className="space-y-6" data-testid="gift-offer-schedule-editor">
            <div className="flex grow items-center justify-between">
                <h5 className="font-bold">Window Definition</h5>
                <SecondaryButton size="small" icon={<PlusIcon />} onClick={handleCreatePurchaseWindowSchedule}>
                    Add {truncate(title)}
                </SecondaryButton>
            </div>

            {windowSchedule ? (
                <div className="rounded-lg border border-gray-light p-5">
                    <div className="flex items-center justify-between">
                        <h3>{windowSchedule.name ? windowSchedule.name : `${title} Schedule`}</h3>

                        <div className="space-x-2">
                            <Badge size="medium" color="success">
                                Available
                            </Badge>

                            <SecondaryButton
                                size="small"
                                data-testid="gift-schedule-editor-window-edit-button"
                                onClick={() => {
                                    setSelectedWindowSchedule(windowSchedule);
                                }}
                            >
                                <PenIcon />
                            </SecondaryButton>

                            <SecondaryButton size="small" onClick={handleRemovePurchaseWindowSchedule}>
                                <TrashIcon />
                            </SecondaryButton>
                        </div>
                    </div>

                    <BaseScheduleSummary productStyling schedule={windowSchedule} />
                </div>
            ) : (
                <div className="flex items-center justify-between">
                    <div className="text-gray-darker">No restrictions</div>
                </div>
            )}

            <div className="flex grow items-center justify-between">
                <h5 className="font-bold">Blackout Restrictions</h5>

                <SecondaryButton icon={<PlusIcon />} size="small" onClick={handleCreateBlackoutSchedule}>
                    Add Blackout Restrictions
                </SecondaryButton>
            </div>

            <div className="space-y-5">
                {blackoutSchedules.map((schedule, index) => (
                    <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        className="rounded-lg border border-gray-light p-5"
                    >
                        <div className="flex items-center justify-between">
                            <h3>{schedule.name ? <NoTranslate>schedule.name</NoTranslate> : `${title} Schedule`}</h3>

                            <div className="space-x-2">
                                <Badge size="medium" color="danger">
                                    Unavailable
                                </Badge>

                                <SecondaryButton
                                    size="small"
                                    onClick={() => {
                                        setSelectedBlackoutScheduleIndex(index);
                                    }}
                                >
                                    <PenIcon />
                                </SecondaryButton>

                                <SecondaryButton
                                    size="small"
                                    onClick={() => {
                                        handleRemoveBlackoutSchedule(index);
                                    }}
                                >
                                    <TrashIcon />
                                </SecondaryButton>
                            </div>
                        </div>

                        <BaseScheduleSummary productStyling showDetails schedule={schedule} />
                    </div>
                ))}

                <div className="flex items-center justify-between">
                    {blackoutSchedules.length === 0 ? <div className="text-gray-darker">No blackouts</div> : null}
                </div>
            </div>

            <BaseScheduleEditorModal
                title={title}
                description={description}
                subtitle="Window Definition"
                isOpen={selectedWindowSchedule !== null}
                initialValues={selectedWindowSchedule!}
                onApply={handleUpdatePurchaseWindowSchedule}
                onClose={() => {
                    setSelectedWindowSchedule(null);
                }}
            />

            <BaseScheduleEditorModal
                title={title}
                description={description}
                subtitle="Blackout Restrictions"
                isOpen={showCreateBlackoutScheduleModal || selectedBlackoutScheduleIndex !== null}
                initialValues={selectedBlackoutSchedule}
                onApply={handleUpdateBlackoutSchedule}
                onClose={() => {
                    setSelectedBlackoutScheduleIndex(null);
                    setShowCreateBlackoutScheduleModal(false);
                }}
            />
        </div>
    );
};
